import {createStyles} from '@mantine/core';
import React, {ReactNode, useRef, useState} from 'react';

import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import ProcedureRunDesignFlowchart from 'studies/typed/shared/ProcedureRunDesignFlowchart';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

const LEFT_PANEL_SIZE = SidepanelSize.MEDIUM; // TODO (BNCH-156225): Decrease to SMALL and fix resulting styling issues
const RIGHT_PANEL_SIZE = SidepanelSize.MEDIUM;

interface ProcedureRunDesignFlowchartEditorProps {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  leftPanelChildren: ReactNode;
  rightPanelChildren: ReactNode;
}

export default function ProcedureRunDesignFlowchartEditor(
  props: ProcedureRunDesignFlowchartEditorProps
): JSX.Element {
  const {classes, cx} = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [selectedLeftSidebarIconIndex, setSelectedLeftSidebarIconIndex] = useState<number | null>(0);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartEditor', element: 'root'})}
    >
      <FlowchartSidebar
        icons={[
          {
            name: 'create',
            onClick: () => {
              setIsLeftPanelOpen(true);
            },
          },
        ]}
        selectedIconIndex={selectedLeftSidebarIconIndex}
        setSelectedIconIndex={setSelectedLeftSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        sidepanelName="Run Plan"
        isOpen={isLeftPanelOpen}
        onClose={() => {
          setIsLeftPanelOpen(false);
          setSelectedLeftSidebarIconIndex(null);
        }}
        size={LEFT_PANEL_SIZE}
        showExpandCollapseButtons={false}
      >
        {props.leftPanelChildren}
      </FlowchartContextPanel>
      <div className={cx(classes.flowchartContainer)}>
        <ProcedureRunDesignFlowchart
          conditions={props.conditions}
          methods={props.methods}
          controlsShiftLeft={0}
          onNodeClick={() => {
            setIsRightPanelOpen(true);
          }}
        />
      </div>
      <FlowchartContextPanel
        panelSide={PanelSide.RIGHT}
        sidepanelName="Design"
        isOpen={isRightPanelOpen}
        onClose={() => {
          setIsRightPanelOpen(false);
        }}
        size={RIGHT_PANEL_SIZE}
        showExpandCollapseButtons={true}
        resizable={true}
        resizeStorageKey="studies-procedure-design-right-sidepanel-width"
        resizeContainerRef={containerRef}
      >
        {props.rightPanelChildren}
      </FlowchartContextPanel>
    </div>
  );
}

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  flowchartContainer: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
  },
}));
